import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const BASE_API = process.env.REACT_APP_BASE_API;
const KRANKQUE_SECRET_KEY = process.env.REACT_APP_KRANKQUE_SECRET_KEY;
const KRANKQUE_APPLICATION_ID = process.env.REACT_APP_KRANKQUE_APPLICATION_ID;

export interface ReviewLocation {
  longitude: number;
  latitude: number;
}

export interface GetReviewsParams {
  accessToken: string;
  params: {
    page: number;
    limit: number;
    sort: 'asc' | 'desc';
    location: ReviewLocation | null;
    query?: string;
  };
}

export interface ReviewCardData {
  id: string;
  description: string;
  createdAt: string;
  rating: number;
  photos?: string[];

  customer: {
    id: string;
    name: string;
    avatarKey?: string | null;
    crmId?: string;
  };

  owner: {
    id: string;
    email: string;
    phone: string;
    firstName: string;
    avatarKey?: string | null;
    crmId?: string;
    accountType?: 'business' | 'personal'; 
    company: {
      companyName: string;
      picture?: string; 
    };
  };
}

interface GetReviewsResponse {
  success: boolean;
  message: string;
  data: ReviewCardData[];
}

export interface GetCustomerReviewsParams {
  accessToken: string;
  page: number;
  limit: number;
  sort: 'asc' | 'desc';
  id: string;
}

interface GetCustomerReviewsResponse {
  success: boolean;
  message: string;
  data: ReviewCardData[];
}

interface CreateReviewRequest {
  accessToken: string;
  body: {
    customerId: string;
    rating: number;
    description: string;
    photos: string[];
    location?: {
      longitude: number;
      latitude: number;
    };
  };
}

interface CreateReviewResponse {
  success: boolean;
  message: string;
}

export const reviewSlice = createApi({
  reducerPath: 'review',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_API,
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');

      if (KRANKQUE_SECRET_KEY) {
        headers.set('krankque-secret-key', KRANKQUE_SECRET_KEY);
      }

      if (KRANKQUE_APPLICATION_ID) {
        headers.set('krankque-application-id', KRANKQUE_APPLICATION_ID);
      }

      return headers;
    }
  }),
  tagTypes: ['REVIEW'],
  endpoints: (builder) => ({
    getReviews: builder.query<GetReviewsResponse, GetReviewsParams>({
      query: (req) => ({
        url: '/reviews',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        },
        body: req.params
      }),
      providesTags: ['REVIEW']
    }),

    getCustomerReviews: builder.query<GetCustomerReviewsResponse, GetCustomerReviewsParams>({
      query: ({ accessToken, page, limit, sort, id }) => ({
        url: `/customer-reviews/${id}?page=${page}&limit=${limit}&sort=${sort}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }),
      providesTags: ['REVIEW']
    }),

    createReview: builder.mutation<CreateReviewResponse, CreateReviewRequest>({
      query: ({ accessToken, body }) => ({
        url: '/review',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        body
      }),
      invalidatesTags: ['REVIEW']
    }),

    getUserReviews: builder.query<GetCustomerReviewsResponse, GetCustomerReviewsParams>({
      query: ({ accessToken, page, limit, sort, id }) => ({
        url: `/user-reviews/${id}?page=${page}&limit=${limit}&sort=${sort}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }),
      providesTags: ['REVIEW']
    }),
    deleteReview: builder.mutation<{ success: boolean; message: string }, { accessToken: string; id: string }>({
      query: ({ accessToken, id }) => ({
        url: `/review/${id}`,
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }),
      invalidatesTags: ['REVIEW']
    }),
    updateReview: builder.mutation<
      { success: boolean; message: string },
      { accessToken: string; reviewId: string; body: { description: string; rating: number; photos: string[] } }
    >({
      query: ({ accessToken, reviewId, body }) => ({
        url: `/review/${reviewId}`,
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        body
      }),
      invalidatesTags: ['REVIEW']
    })
  })
});

export const {
  useGetReviewsQuery,
  useLazyGetReviewsQuery,
  useGetCustomerReviewsQuery,
  useGetUserReviewsQuery,
  useCreateReviewMutation,
  useLazyGetCustomerReviewsQuery,
  useLazyGetUserReviewsQuery,
  useDeleteReviewMutation,
  useUpdateReviewMutation
} = reviewSlice;
